// useLocale — read/change the current language and the hotel's allowed set.
//
// Returns:
//   language          current language code, e.g. 'en'
//   setLanguage(code) switch to that language; persists to localStorage
//                     and (best-effort) to the user's profile so the next
//                     server-rendered email/PDF respects the choice.
//   supported         hotel.supported_languages — full list when the
//                     hotel operates multi-lingual, single entry when not.
//   showSwitcher      true iff the user can actually change the language
//                     (more than one option). Hide the switcher when false.
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from './index';

const STORAGE_HOTEL_SUPPORTED = 'i18nHotelSupported';
// Custom event fired in-tab whenever supported languages change. The native
// `storage` event only fires across tabs, so without this every component
// instantiating useLocale would keep its own stale copy until a refresh.
const SUPPORTED_CHANGED_EVENT = 'i18n-supported-changed';

const _readSupported = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_HOTEL_SUPPORTED) || '[]');
    if (Array.isArray(cached) && cached.length) return cached;
  } catch { /* fall through */ }
  return ['en'];
};

export function useLocale() {
  const { i18n } = useTranslation();

  // Hotel.supported_languages comes from the Settings → Languages
  // endpoint and is cached in localStorage so the switcher renders
  // before the API call finishes.
  const [supported, setSupportedState] = useState(_readSupported);

  // Stay in sync with both cross-tab storage events and same-tab
  // updates from sibling components (LanguageSettings save, etc).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_HOTEL_SUPPORTED) {
        try { setSupportedState(JSON.parse(e.newValue || '[]')); } catch {}
      }
    };
    const onCustom = (e) => {
      if (Array.isArray(e?.detail)) setSupportedState(e.detail);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SUPPORTED_CHANGED_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SUPPORTED_CHANGED_EVENT, onCustom);
    };
  }, []);

  const setSupported = useCallback((codes) => {
    const list = Array.from(new Set(['en', ...(codes || [])]));
    localStorage.setItem(STORAGE_HOTEL_SUPPORTED, JSON.stringify(list));
    setSupportedState(list);
    // Notify other useLocale instances in this tab so they update too.
    window.dispatchEvent(new CustomEvent(SUPPORTED_CHANGED_EVENT, { detail: list }));
  }, []);

  const setLanguage = useCallback(async (code) => {
    if (!supported.includes(code)) return;
    await i18n.changeLanguage(code);
    // Best-effort sync to backend so server-rendered emails/PDFs match.
    // We import lazily to avoid a hard dep cycle with ApiClient.
    try {
      const { default: apiClient } = await import('../service/ApiClient');
      await apiClient.patch('/auth/me/', { preferred_language: code });
    } catch { /* non-blocking — UI already switched */ }
  }, [i18n, supported]);

  const language = (i18n.language || 'en').split('-')[0];
  const showSwitcher = supported.length > 1;
  const options = SUPPORTED_LANGUAGES.filter((l) => supported.includes(l.code));

  return { language, setLanguage, supported, setSupported, options, showSwitcher };
}
